import { DateTime } from 'luxon';
import type { IntegrationType } from '@/api/types/Integrations';
import type { ISalesChannel } from '@/api/model/SalesChannel';
import SalesChannel from '@/api/model/SalesChannel';

export interface IntegrationJson {
    created_at: string;
    has_daily_sync: boolean;
    track_problem_orders: boolean;
    id: number;
    name: string;
    url?: string;
    region_code: string;
    seller_partner_id: string;
    type: IntegrationType;
    updated_at: string;
    uuid: string;
    auth_token: string;
    client_key: string;
    client_secret: string;
    sales_channels: ISalesChannel[];
    orders_synced_at?: string;
    shipments_synced_at?: string;
    inventory_synced_at?: string;
    transfers_synced_at?: string;
    auth_url?: string;
    // Webhook fields now on integration
    webhook_status?: 'active' | 'inactive' | 'error' | null;
    last_webhook_check?: string | null;
    webhook_error?: string | null;
    webhook_delivery_count?: number;
    webhook_failure_count?: number;
    last_webhook_delivery?: string | null;
    webhook_config?: string | null;
    consecutive_sync_errors?: number;
    last_sync_error_message?: string | null;
    last_sync_error_at?: string | null;
}

export default class Integration {
    created_at: DateTime = DateTime.local();
    has_daily_sync = true;
    track_problem_orders = true;
    id = 0;
    name?: string;
    url?: string;
    region_code = 'Unknown';
    seller_partner_id?: string;
    type: IntegrationType = 'manual';
    updated_at: DateTime = DateTime.local();
    auth_token = '';
    client_key = '';
    client_secret = '';
    uuid?: string;
    sales_channels: SalesChannel[] = [];
    orders_synced_at: DateTime | null = null;
    shipments_synced_at: DateTime | null = null;
    inventory_synced_at: DateTime | null = null;
    transfers_synced_at: DateTime | null = null;
    auth_url?: string;
    // Webhook fields
    webhook_status: 'active' | 'inactive' | 'error' | null = null;
    last_webhook_check: DateTime | null = null;
    webhook_error: string | null = null;
    webhook_delivery_count = 0;
    webhook_failure_count = 0;
    last_webhook_delivery: DateTime | null = null;
    webhook_config: string | null = null;
    consecutive_sync_errors = 0;
    last_sync_error_message: string | null = null;
    last_sync_error_at: DateTime | null = null;

    toJSON(): IntegrationJson {
        return Object.assign({} as IntegrationJson, this, {});
    }

    static fromJSON(json: IntegrationJson): Integration {
        const integration = Object.create(Integration.prototype);
        return Object.assign(integration, json, {
            created_at: json.created_at ? DateTime.fromISO(json.created_at) : DateTime.now(),
            updated_at: json.updated_at ? DateTime.fromISO(json.updated_at) : DateTime.now(),
            sales_channels: json.sales_channels ? json.sales_channels.map(SalesChannel.fromJSON) : [],
            client_key: json.client_key ? json.client_key : '',
            inventory_synced_at: json.inventory_synced_at ? DateTime.fromISO(json.inventory_synced_at) : null,
            transfers_synced_at: json.transfers_synced_at ? DateTime.fromISO(json.transfers_synced_at) : null,
            orders_synced_at: json.orders_synced_at ? DateTime.fromISO(json.orders_synced_at) : null,
            shipments_synced_at: json.shipments_synced_at ? DateTime.fromISO(json.shipments_synced_at) : null,
            last_webhook_check: json.last_webhook_check ? DateTime.fromISO(json.last_webhook_check) : null,
            last_webhook_delivery: json.last_webhook_delivery ? DateTime.fromISO(json.last_webhook_delivery) : null,
            last_sync_error_at: json.last_sync_error_at ? DateTime.fromISO(json.last_sync_error_at) : null,
        });
    }
}

export type WithIntegration = {
    report_source: Integration;
};
